import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  UiArguments,
} from "shared/provider-webworker";

import { ReplicaLookupService } from "../../impl/ReplicaLookupService";
import { IAccountPromiseLookupService } from "../IAccountPromiseLookupService";
import { AutoCompleteLabelValueReplicaLookupService } from "./AutoCompleteLabelValueReplicaLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { AutoCompleteLabelValue } from "../../../model/core/AutoCompleteLabelValue";

@Injectable()
export class AccountReplicaLookupService
  extends AutoCompleteLabelValueReplicaLookupService<string, CubiqTS.AccountImpl>
  implements IAccountPromiseLookupService
{
  constructor(protected factory: ClientMessageBrokerFactory) {
    super(factory, CubiqTS.AccountImpl, "AccountImpl");
  }

  getAccount(id: string, type?: ClientAccountVisibilityType | null): Promise<CubiqTS.AccountImpl> {
    let request = new UiArguments("getAccount");
    let arg1 = new FnArg(id, PRIMITIVE);

    request.args = [arg1, new FnArg(type, PRIMITIVE)];
    return this.broker.runOnService(request, this.VALUE);
  }

  getAccountsForClient(id: string): Promise<CubiqTS.AccountImpl[]> {
    let request = new UiArguments("getAccountsForClient");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAccount(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccount");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  /* same as lookupAccount() but more efficient */
  lookupAccountsForId(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccountsForId");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAccountsForName(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccountsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAccountsForBookingCode(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccountsForBookingCode");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAccountsForContact(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccountsForContact");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupAccountsForClient(
    namePrefix: string,
    clientCd: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupAccountsForClient");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(clientCd, PRIMITIVE);
    let arg3 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2, arg3];
    return this.broker.runOnService(request, this.VALUE);
  }

  getBreakAccounts(salesGroup: string): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("getBreakAccounts");
    let arg1 = new FnArg(salesGroup, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, this.VALUE);
  }

  getBreakAndClientAccounts(
    clientCd: string,
    salesGroup: string,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("getBreakAndClientAccounts");
    let arg1 = new FnArg(clientCd, PRIMITIVE);
    let arg2 = new FnArg(salesGroup, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  /* Active Accounts */
  getActiveAccount(
    id: string,
    type?: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.AccountImpl> {
    let request = new UiArguments("getActiveAccount");
    let arg1 = new FnArg(id, PRIMITIVE);

    request.args = [arg1, new FnArg(type, PRIMITIVE)];
    return this.broker.runOnService(request, this.VALUE);
  }

  getActiveAccountsForClient(id: string): Promise<CubiqTS.AccountImpl[]> {
    let request = new UiArguments("getActiveAccountsForClient");
    let arg1 = new FnArg(id, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, this.VALUE);
  }

  getActiveBreakAccounts(salesGroup: string): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("getActiveBreakAccounts");
    let arg1 = new FnArg(salesGroup, PRIMITIVE);
    request.args = [arg1];
    return this.broker.runOnService(request, this.VALUE);
  }

  getActiveBreakAndClientAccounts(
    clientCd: string,
    salesGroup: string,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("getActiveBreakAndClientAccounts");
    let arg1 = new FnArg(clientCd, PRIMITIVE);
    let arg2 = new FnArg(salesGroup, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupActiveAccountsForId(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupActiveAccountsForId");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }

  lookupActiveAccountsForName(
    namePrefix: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    let request = new UiArguments("lookupActiveAccountsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }
  // not in use
  /*    lookupActiveAccountsForBookingCode(namePrefix:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        let request = new UiArguments('lookupActiveAccountsForBookingCode');
        let arg1 = new FnArg(namePrefix, PRIMITIVE);
        let arg2 = new FnArg(maxResults, PRIMITIVE);
        request.args = [arg1, arg2];
        return this.broker.runOnService(request, this.VALUE);
    }

    lookupActiveAccountsForContact(namePrefix:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        let request = new UiArguments('lookupActiveAccountsForContact');
        let arg1 = new FnArg(namePrefix, PRIMITIVE);
        let arg2 = new FnArg(maxResults, PRIMITIVE);
        request.args = [arg1, arg2];
        return this.broker.runOnService(request, this.VALUE);
    }
    
    lookupActiveAccountsForClient(namePrefix:string, clientCd:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        let request = new UiArguments('lookupActiveAccountsForClient');
        let arg1 = new FnArg(namePrefix, PRIMITIVE);
        let arg2 = new FnArg(clientCd, PRIMITIVE);
        let arg3 = new FnArg(maxResults, PRIMITIVE);
        request.args = [arg1, arg2, arg3];
        return this.broker.runOnService(request, this.VALUE);
    } */

  lookupBMarkAccountsForId(id: string, maxResults: number): Promise<CubiqTS.AccountImpl[]> {
    let request = new UiArguments("lookupBMarkAccountsForId");
    let arg1 = new FnArg(id, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }
  lookupBMarkAccountsForName(
    namePrefix: string,
    maxResults: number,
  ): Promise<CubiqTS.AccountImpl[]> {
    let request = new UiArguments("lookupBMarkAccountsForName");
    let arg1 = new FnArg(namePrefix, PRIMITIVE);
    let arg2 = new FnArg(maxResults, PRIMITIVE);
    request.args = [arg1, arg2];
    return this.broker.runOnService(request, this.VALUE);
  }
}
